import { Fragment, useCallback, useEffect, useRef, useState } from "react";

import { Button } from "../components/ui/button";
import { Badge, Table, Td, Th } from "../components/ui/misc";
import { renderAnnotatedPage } from "../lib/annotated-page";
import { api } from "../lib/api";
import { boxColorFor } from "../lib/cabinet-colors";
import { centsToPlain, downloadCsv, exportFileBase, toCsv } from "../lib/csv";
import { roomLabel } from "../lib/room-labels";
import { formatMoney } from "../lib/utils";

type PrintPayload = Awaited<ReturnType<typeof api.print>>;

/**
 * 客户报价单（打印视图）。
 *
 * 规矩（见报价领域不变量 §23-25）：
 * - 这是**唯一**的客户输出，服务端不生成 PDF，打印交给浏览器自己的 print；
 * - 客户页英文 + 计划货币（默认 USD）+ EXW Foshan，内部成本与系数一律不出现
 *   （接口 `/print` 本来就不返回，这里也不做任何反推）；
 * - 没填尺寸的条目标成 TBC、不参与合计，并写进免责说明。
 */
/** 客户版文案（中英切换）；工厂版一律中文 */
const TEXT = {
  en: {
    quotation: "Cabinet Quotation",
    date: "Date",
    currency: "Currency",
    terms: "Terms",
    item: "Item",
    room: "Room",
    type: "Type",
    size: "Size (W×H×D mm)",
    qty: "Qty",
    amount: "Amount",
    subtotal: "Subtotal (confirmed items)",
    awaiting: "Items awaiting sizes",
    excluded: "excluded",
    total: "Total",
    noItems: "No items yet.",
    floorPlan: "Floor plan",
    planNote:
      "Layout plan — cabinet outlines are shown for reference; scope and prices are listed in the quotation below.",
    disclaimer:
      "This document is a budget and review draft, not a final commercial offer. Final quotation is subject to on-site measurements, appliance specifications, approved samples, hardware models, stone layout and production drawings.",
    validity: "Validity: 30 days from the date above.",
    packaging: "Packaging",
    packagingNote: "Already included in the item prices above — shown for reference.",
    scopeCabinet: "Cabinet",
    scopeDoor: "Door",
    scopePanel: "Visible panel",
    scopeCountertop: "Countertop",
    scopeHardware: "Hardware",
    scopeAccessory: "Accessories",
    scopeLed: "LED strip",
    paymentDelivery: "Payment & delivery",
    payment: "Payment",
    /** 付款方式按方案选的 code 出条款（见 contracts 的 PAYMENT_TERMS_CODES） */
    paymentTermsText: {
      deposit_50_50: "50% deposit with order, 50% balance before loading.",
      deposit_5000_70:
        "RMB 5,000 deposit to start the shop drawings and 3D; 70% after the drawings are confirmed, balance settled before shipment.",
    },
    delivery: "Delivery",
    deliveryTerms: "30–45 days production after drawing details are confirmed and the deposit is received.",
    assemblyLabel: "Assembly & packing (7% of goods)",
    notes: "Notes",
    // 备注里不写货币（币种在表头/合计行按方案自动出，写死会跟单子对不上），
    // 也不解释运费关税怎么收——物流与安装如果客户要，我们另收费，不在这里承诺。
    notePreliminary:
      "This is a preliminary quotation. Final pricing follows the approved shop drawings and samples.",
    packingFlatPack:
      "Packing: flat-pack as standard, hinges included. Fully assembled shipment is available at 7% of goods value.",
    packingAssembled:
      "Packing: fully assembled shipment — 7% of goods value is charged and included in the total above.",
    noteValidity: "Validity: 30 days from the date above.",
    csv: {
      space: "Floor / Space",
      width: "Width (mm)",
      height: "Height (mm)",
      depth: "Depth (mm)",
      sizeStatus: "Size status",
      pending: "to be confirmed",
      goods: "Subtotal (goods)",
      assembly: "Assembly & packing",
      total: "Total",
      terms: "Payment & delivery",
      disclaimer: "Disclaimer",
    },
    pcs: "pcs",
  },
  zh: {
    quotation: "橱柜报价单",
    date: "日期",
    currency: "币种",
    terms: "贸易条款",
    item: "编号",
    room: "区域",
    type: "柜型",
    size: "尺寸（宽×高×深 mm）",
    qty: "数量",
    amount: "金额",
    subtotal: "小计（已填尺寸条目）",
    awaiting: "待确认尺寸的条目",
    excluded: "未计入",
    total: "合计",
    noItems: "暂无条目。",
    floorPlan: "户型图",
    planNote: "平面布置图：图上只画出柜体位置，范围与价格见下面的报价明细。",
    disclaimer:
      "本文件为预算与审核底稿，不构成正式商务报价。最终报价以现场尺寸、电器规格、确认样板、五金型号、石材排版和生产图为准。",
    validity: "有效期：自上述日期起 30 天。",
    packaging: "包装",
    packagingNote: "已包含在上面的柜体价格里，这里只是列明。",
    scopeCabinet: "柜体",
    scopeDoor: "门板",
    scopePanel: "见光板",
    scopeCountertop: "台面",
    scopeHardware: "五金",
    scopeAccessory: "配件",
    scopeLed: "灯带",
    paymentDelivery: "付款方式与交货期",
    payment: "付款方式",
    paymentTermsText: {
      deposit_50_50: "下单付 50% 定金，余款 50% 在货好装柜前付清。",
      deposit_5000_70:
        "客户确认初步报价方案后支付定金人民币 5000 元整，出效果图与深化图纸；图纸确认无误后支付 70%，出货前付清余款。",
    },
    delivery: "交货期",
    deliveryTerms: "确认图纸细节、收齐定金后 30–45 天生产周期。",
    assemblyLabel: "整装出货打包组装费（货值的 7%）",
    notes: "报价说明",
    notePreliminary: "以上为初步报价，最终以深化图纸与双方确认样板为准。",
    packingFlatPack: "包装：默认散装出货，铰链按标配含在内；如需整装出货，按货值 7% 收取打包组装费。",
    packingAssembled: "包装：本单为整装出货，按货值 7% 收取打包组装费，已列在合计上方。",
    noteValidity: "有效期：自上述日期起 30 天。",
    csv: {
      space: "楼层/空间",
      width: "宽(mm)",
      height: "高(mm)",
      depth: "深(mm)",
      sizeStatus: "尺寸状态",
      pending: "尺寸待定",
      goods: "货值",
      assembly: "打包组装费",
      total: "合计",
      terms: "付款方式与交货期",
      disclaimer: "免责声明",
    },
    pcs: "件",
  },
} as const;

/** 含料明细的分类名（客户页按当前语言出，绝不印内部槽位代码） */
const SCOPE_LABEL = {
  cabinet: "scopeCabinet",
  door: "scopeDoor",
  panel: "scopePanel",
  countertop: "scopeCountertop",
  hardware: "scopeHardware",
  accessory: "scopeAccessory",
} as const;

export function PrintView({
  planId,
  variant = "customer",
}: {
  planId: string;
  variant?: "customer" | "factory";
}) {
  const [payload, setPayload] = useState<PrintPayload | null>(null);
  const [language, setLanguage] = useState<"en" | "zh">("en");
  /** 工厂版要明细：直接用后台接口取条目 + 组件（含成本、公式快照） */
  const [factory, setFactory] = useState<Awaited<ReturnType<typeof api.plan>> | null>(null);
  /** 有画框的图纸页（渲染成图片，放在报价单前面） */
  const [planPages, setPlanPages] = useState<Array<{ key: string; pageLabel: string; dataUrl: string }>>([]);
  const [pagesReady, setPagesReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * 柜型的中英名：报价单的「柜型 / Type」列不能直接印内部代码（base / wardrobe），
   * 工厂版出中文名，客户版按当前语言出中文名或英文名。
   */
  const [cabinetTypeNames, setCabinetTypeNames] = useState<Record<string, { zh: string; en: string }>>({});
  const printedRef = useRef(false);
  /** 进打印页之前的标签页标题，离开时还原（别把后台的标题改花） */
  const pristineTitleRef = useRef<string | null>(null);

  /*
   * 打印/存成 PDF 的文件名 = 浏览器用的 `document.title`。
   * 标题 = 方案名 + 日期时间（到分钟）：客户版 `Shahar 2026-09-30 18-37`，
   * 工厂版再加「工厂单」后缀（两份一起打印不会互相盖掉，同一天打印多次也不会重名）。
   * 文件名里不能用的字符换成短横；冒号也不能用，所以时间写成 18-37。
   */
  const applyPrintTitle = useCallback(() => {
    const name = payload?.plan.name ?? factory?.plan.name;
    if (!name) return;
    if (pristineTitleRef.current === null) pristineTitleRef.current = document.title;
    document.title = exportFileBase(name, new Date(), variant === "factory" ? " 工厂单" : "");
  }, [payload?.plan.name, factory?.plan.name, variant]);

  useEffect(() => {
    applyPrintTitle();
  }, [applyPrintTitle]);

  useEffect(
    () => () => {
      if (pristineTitleRef.current !== null) document.title = pristineTitleRef.current;
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await api.print(planId);
        if (!cancelled) setPayload(data);
      } catch (caught) {
        if (!cancelled) setError(caught instanceof Error ? caught.message : "加载失败");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [planId]);

  useEffect(() => {
    if (variant !== "factory") return;
    let cancelled = false;
    void api
      .plan(planId)
      .then((data) => {
        if (!cancelled) setFactory(data);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(caught instanceof Error ? caught.message : "加载失败");
      });
    return () => {
      cancelled = true;
    };
  }, [planId, variant]);

  useEffect(() => {
    let cancelled = false;
    void api
      .cabinetTypes()
      .then((data) => {
        if (cancelled) return;
        setCabinetTypeNames(
          Object.fromEntries(
            data.cabinetTypes.map((type) => [
              type.code,
              { zh: type.name ?? type.code, en: type.name_en ?? type.name ?? type.code },
            ]),
          ),
        );
      })
      .catch(() => {
        /* 取不到就用代码兜底，不影响出单 */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 点打印时按"这一刻"重算标题：在页面上停一会儿再打印，文件名也是打印当时的时间
  const print = useCallback(() => {
    applyPrintTitle();
    window.print();
  }, [applyPrintTitle]);

  // 客户版先放"画了框的户型图页"：只取有条目的页，按页渲染成带框图片
  useEffect(() => {
    // 等方案设置回来再决定：关掉"附平面图"就完全不渲染图纸页（效果图放报价单里不好看）
    if (!payload) return;
    if (!payload.plan.includeFloorPlan) {
      setPlanPages([]);
      setPagesReady(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const detail = await api.plan(planId);
        const rendered: Array<{ key: string; pageLabel: string; dataUrl: string }> = [];
        for (const drawing of detail.drawings) {
          const drawingItems = detail.items.filter((item) => item.drawing_id === drawing.id);
          const pageIndexes = [...new Set(drawingItems.map((item) => item.page_index))].sort((a, b) => a - b);
          for (const pageIndex of pageIndexes) {
            if (cancelled) return;
            const boxes = drawingItems
              .filter((item) => item.page_index === pageIndex)
              .map((item) => ({
                x: item.box_x,
                y: item.box_y,
                w: item.box_w,
                h: item.box_h,
                label: item.label,
                cabinetType: item.cabinet_type_code,
                rotationDeg: item.rotation_deg ?? 0,
              }));
            const image = await renderAnnotatedPage({
              url: `/api/v1/media/${drawing.file_key}`,
              isImage: (drawing.mime_type ?? "application/pdf").startsWith("image/"),
              pageIndex,
              boxes,
            });
            rendered.push({
              key: `${drawing.id}-${pageIndex}`,
              pageLabel: `${drawing.file_name} · page ${pageIndex + 1}`,
              dataUrl: image.dataUrl,
            });
          }
        }
        if (!cancelled) setPlanPages(rendered);
      } catch {
        // 户型图渲染失败不影响报价单本身
        if (!cancelled) setPlanPages([]);
      } finally {
        if (!cancelled) setPagesReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [planId, payload?.plan.includeFloorPlan]);

  // 打开即调起浏览器打印（工具栏的「预览」就是这个入口）
  useEffect(() => {
    if (!payload || !pagesReady || printedRef.current) return;
    // ?preview=1 只预览不弹打印对话框（也便于自动化测试）
    if (new URLSearchParams(window.location.search).get("preview") === "1") return;
    printedRef.current = true;
    const timer = window.setTimeout(print, 350);
    return () => window.clearTimeout(timer);
  }, [payload, pagesReady, print]);

  if (error) {
    return <p className="p-6 text-sm text-[var(--color-destructive)]">{error}</p>;
  }
  if (!payload) {
    return <p className="p-6 text-sm text-[var(--color-muted-foreground)]">正在准备报价单…</p>;
  }

  const { plan, items, packaging } = payload;
  const currency = plan.currency;
  const pendingItems = items.filter((item) => item.size_status !== "confirmed");
  const counted = items.filter((item) => item.size_status === "confirmed");
  const countedSum = counted.reduce((total, item) => total + item.price_cents, 0);
  // 整装出货的 7% 是单列的附加费，不在条目价里，核对时要加上它
  const mismatch = countedSum + plan.assemblySurchargeCents !== plan.totalPriceCents;
  const today = new Date().toISOString().slice(0, 10);
  const t = TEXT[language];
  /** 报价方式文案：按方案选的 code 取，取不到就退回外贸那套 */
  const paymentTermsText =
    t.paymentTermsText[plan.paymentTerms as keyof typeof t.paymentTermsText] ??
    t.paymentTermsText.deposit_50_50;
  /** 报价说明：初步报价 / 包装（整装出货时换措辞） / 有效期 */
  const noteItems = [
    t.notePreliminary,
    plan.assembledShipment ? t.packingAssembled : t.packingFlatPack,
    t.noteValidity,
  ];
  /** 柜型名：按当前语言出，取不到就回退成代码，绝不印空白 */
  const typeText = (code: string) =>
    language === "en" ? (cabinetTypeNames[code]?.en ?? code) : (cabinetTypeNames[code]?.zh ?? code);
  /** 含料明细的名称：客户页是英文，目录里有英文名就出英文，没有就用中文快照 */
  const nameText = (names: Array<{ zh: string; en: string }>) =>
    names.map((name) => (language === "en" ? name.en : name.zh)).join(" · ");
  /**
   * 一个条目的含料明细：柜体 → 门板 → 见光板 → 台面 → 五金 → 配件 → 灯带。
   * **只列这个条目真正有的类目**——没有的不出现，报价单上不写"不含台面"这种话。
   * 条目一个组件都没有（刚画框还没生成）时返回空数组，不印噪音。
   */
  const materialLines = (materials: (typeof items)[number]["materials"]) => {
    const lines: Array<{ key: string; label: string; value: string }> = [];
    for (const group of materials.groups) {
      if (group.names.length === 0) continue;
      lines.push({ key: group.key, label: t[SCOPE_LABEL[group.key]], value: nameText(group.names) });
    }
    if (materials.ledNames.length > 0) {
      lines.push({ key: "led", label: t.scopeLed, value: nameText(materials.ledNames) });
    }
    return lines;
  };

  /** CSV 里含料明细的固定列顺序（跟 PDF 上那块同源同序） */
  const MATERIAL_KEYS = ["cabinet", "door", "panel", "countertop", "hardware", "accessory"] as const;
  /** 一个条目的材料按固定列铺开；没有的类目留空（报价单上不写"不含"，CSV 同理） */
  const materialColumns = (materials: (typeof items)[number]["materials"]): string[] => {
    const byKey = new Map(materials.groups.map((group) => [group.key, nameText(group.names)]));
    return [
      ...MATERIAL_KEYS.map((key) => byKey.get(key) ?? ""),
      materials.ledNames.length > 0 ? nameText(materials.ledNames) : "",
    ];
  };

  /**
   * 导出 CSV：内容与同版 PDF 一致（同一份数据、同一套名称口径），只是换成 Excel 能筛能算的表格。
   * - 客户版：条目表（楼层/空间、编号、柜型、尺寸、数量、金额）+ 含料明细 + 货值/组装费/合计 + 付款交货与报价说明
   * - 工厂版：逐组件的成本明细（含计价方式与公式参数）+ 每组的条目成本与报价
   */
  const buildCsvRows = (): Array<Array<string | number>> | null => {
    if (variant === "factory") {
      if (!factory) return null;
      const cost = factory.plan.cost_currency;
      const rows: Array<Array<string | number>> = [
        [
          "编号",
          "楼层/空间",
          "柜型",
          "组件",
          "槽位",
          "计价方式",
          "参数",
          "数量",
          "单位",
          `单价(成本 ${cost})`,
          `金额(成本 ${cost})`,
          "条目成本合计",
          `条目报价(${factory.plan.currency})`,
          "目录代码",
          "状态",
        ],
      ];
      for (const item of factory.items) {
        const typeName = cabinetTypeNames[item.cabinet_type_code]?.zh ?? item.cabinet_type_code;
        const components = factory.components.filter((component) => component.item_id === item.id);
        if (components.length === 0) {
          rows.push([
            item.label,
            item.room ?? "",
            typeName,
            "（尚未生成组件）",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            centsToPlain(item.cost_cents),
            centsToPlain(item.price_cents),
            "",
            "",
          ]);
          continue;
        }
        for (const component of components) {
          rows.push([
            item.label,
            item.room ?? "",
            typeName,
            component.name_snapshot,
            component.slot,
            component.calc_method,
            component.params,
            component.quantity,
            component.unit,
            centsToPlain(component.unit_price_cents),
            centsToPlain(component.amount_cents),
            centsToPlain(item.cost_cents),
            centsToPlain(item.price_cents),
            component.catalog_code,
            // 待配置（缺尺寸 / 需手填面积 / 缺少依赖）的行要能一眼看出来，金额按 0 计
            component.state === "ok" ? "" : `待配置：${component.notes ?? "缺参数"}`,
          ]);
        }
      }
      rows.push([
        "合计",
        ...Array.from({ length: 9 }, () => ""),
        centsToPlain(factory.plan.total_cost_cents),
        "",
        centsToPlain(factory.plan.total_price_cents),
        "",
        "",
      ]);
      return rows;
    }

    if (!payload) return null;
    const rows: Array<Array<string | number>> = [
      [
        t.csv.space,
        t.item,
        t.type,
        t.csv.width,
        t.csv.height,
        t.csv.depth,
        t.qty,
        `${t.amount} (${currency})`,
        t[SCOPE_LABEL.cabinet],
        t[SCOPE_LABEL.door],
        t[SCOPE_LABEL.panel],
        t[SCOPE_LABEL.countertop],
        t[SCOPE_LABEL.hardware],
        t[SCOPE_LABEL.accessory],
        t.scopeLed,
        t.csv.sizeStatus,
      ],
    ];
    for (const item of items) {
      const pending = item.size_status !== "confirmed";
      rows.push([
        roomLabel(item.room, language) ?? "",
        item.label,
        typeText(item.cabinet_type_code),
        pending ? "" : (item.width_mm ?? ""),
        pending ? "" : (item.height_mm ?? ""),
        pending ? "" : (item.depth_mm ?? ""),
        item.quantity,
        pending ? "" : centsToPlain(item.price_cents),
        ...materialColumns(item.materials),
        pending ? t.csv.pending : "",
      ]);
    }
    // 合计区（金额放在"金额"那一列，Excel 里直接求和/对账）
    rows.push([t.csv.goods, ...Array.from({ length: 6 }, () => ""), centsToPlain(plan.goodsCents)]);
    if (plan.assemblySurchargeCents > 0) {
      rows.push([
        t.csv.assembly,
        ...Array.from({ length: 6 }, () => ""),
        centsToPlain(plan.assemblySurchargeCents),
      ]);
    }
    rows.push([t.csv.total, ...Array.from({ length: 6 }, () => ""), centsToPlain(plan.totalPriceCents)]);
    // 条款与说明：跟 PDF 上的付款/交货/报价说明同一份文案
    rows.push([t.payment, paymentTermsText]);
    rows.push([t.delivery, t.deliveryTerms]);
    for (const note of noteItems) rows.push([t.notes, note]);
    rows.push([t.csv.disclaimer, t.disclaimer]);
    return rows;
  };

  /** 导出 CSV：文件名与"打印/存 PDF"同一套（方案名 + 日期时间） */
  const exportCsv = () => {
    const name = payload?.plan.name ?? factory?.plan.name;
    if (!name) return;
    const rows = buildCsvRows();
    if (!rows) return;
    const base = exportFileBase(name, new Date(), variant === "factory" ? " 工厂单" : "");
    downloadCsv(`${base}.csv`, toCsv(rows));
  };

  const toolbar = (
    <div className="no-print mb-6 flex flex-wrap items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-muted)] p-3">
      <Button size="sm" onClick={print}>
        打印 / 存 PDF
      </Button>
      <Button size="sm" variant="outline" onClick={exportCsv}>
        导出 CSV
      </Button>
      <Button size="sm" variant="outline" onClick={() => window.close()}>
        关闭
      </Button>
      {variant === "customer" ? (
        // 客户版：中英文可切换（工厂版固定中文）
        <span className="flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-white p-0.5">
          <Button
            size="sm"
            variant={language === "en" ? "default" : "ghost"}
            onClick={() => setLanguage("en")}
          >
            English
          </Button>
          <Button
            size="sm"
            variant={language === "zh" ? "default" : "ghost"}
            onClick={() => setLanguage("zh")}
          >
            中文
          </Button>
        </span>
      ) : (
        <span className="text-xs text-[var(--color-muted-foreground)]">
          工厂版（中文 · 含计价明细与公式）
        </span>
      )}
      {mismatch ? (
        <span className="text-xs text-amber-700">
          内部提醒：明细合计与方案总额不一致，导出前请先在方案里重算一次。
        </span>
      ) : null}
    </div>
  );

  // ---------------- 工厂版：中文 + 每个柜体的计价明细（含公式参数） ----------------
  if (variant === "factory") {
    if (!factory) {
      return (
        <div className="mx-auto max-w-[900px] p-8">
          {toolbar}
          <p className="text-sm text-[var(--color-muted-foreground)]">正在准备工厂制作单…</p>
        </div>
      );
    }
    const { plan: factoryPlan, items: factoryItems, components: factoryComponents } = factory;
    /** 图上出现过的柜型 → 配色图例（车间看颜色对柜型） */
    const factoryLegend = [...new Set(factoryItems.map((item) => item.cabinet_type_code))].map((code) => ({
      code,
      color: boxColorFor(code),
      name: cabinetTypeNames[code]?.zh ?? code,
    }));
    /** 包装备料汇总：纸箱 / 木箱按组数统计，工厂备料用 */
    const packagingTotals = (() => {
      const map = new Map<
        string,
        { name: string; quantity: number; groups: Set<string>; costCents: number }
      >();
      for (const component of factoryComponents) {
        if (!component.catalog_code.startsWith("MAT-PROJECT-")) continue;
        const entry = map.get(component.catalog_code) ?? {
          name: component.name_snapshot,
          quantity: 0,
          groups: new Set<string>(),
          costCents: 0,
        };
        entry.quantity += component.quantity;
        entry.groups.add(component.item_id);
        entry.costCents += component.amount_cents;
        map.set(component.catalog_code, entry);
      }
      return [...map.entries()].map(([code, entry]) => ({
        code,
        name: entry.name,
        quantity: entry.quantity,
        groups: entry.groups.size,
        costCents: entry.costCents,
      }));
    })();
    return (
      <div className="mx-auto max-w-[900px] bg-white p-8 text-[13px] leading-relaxed text-black print:max-w-none print:p-0">
        {toolbar}
        <header className="flex items-start justify-between border-b-2 border-black pb-4">
          <div>
            <img src="/izza-logo.svg" alt="IZZA" className="mb-2 h-8 w-auto" />
            <h1 className="text-xl font-semibold tracking-wide">工厂制作单（含计价明细）</h1>
            <p className="mt-1 text-sm">{factoryPlan.name}</p>
          </div>
          <div className="text-right text-xs text-neutral-600">
            <p>日期：{today}</p>
            <p>成本币种：{factoryPlan.cost_currency}</p>
            <p>
              报价币种：{factoryPlan.currency} · 汇率 {factoryPlan.exchange_rate}
            </p>
            <p>加价系数：{factoryPlan.markup_factor}</p>
          </div>
        </header>

        {/* 工厂版也带平面图：框的颜色区分柜型（图上不画编号，对号入座看下面的条目表） */}
        {planPages.length > 0 ? (
          <section className="mt-6">
            <div className="mb-2 flex items-baseline justify-between border-b border-black/40 pb-1 text-xs">
              <span className="font-semibold">平面图（标出柜体位置）</span>
              <span className="text-neutral-600">框的颜色代表柜型（图例见下），编号见每组的条目表</span>
            </div>
            {factoryLegend.length > 0 ? (
              <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
                {factoryLegend.map((entry) => (
                  <span key={entry.code} className="flex items-center gap-1">
                    <span
                      className="inline-block h-3 w-3 rounded-sm border"
                      style={{
                        borderColor: entry.color.stroke,
                        backgroundColor: entry.color.fill,
                      }}
                    />
                    {entry.name}
                  </span>
                ))}
              </div>
            ) : null}
            {planPages.map((page, index) => (
              <figure key={page.key} className="mb-4 break-inside-avoid">
                <img src={page.dataUrl} alt={page.pageLabel} className="w-full object-contain" />
                <figcaption className="mt-1 text-[11px] text-neutral-600">
                  第 {index + 1} 张 / 共 {planPages.length} 张 · {page.pageLabel}
                </figcaption>
              </figure>
            ))}
          </section>
        ) : null}

        {factoryItems.length === 0 ? <p className="mt-6 text-sm text-neutral-500">暂无条目。</p> : null}

        {factoryItems.map((item) => {
          const rows = factoryComponents.filter((component) => component.item_id === item.id);
          return (
            <section key={item.id} className="mt-6 break-inside-avoid">
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b border-black pb-1">
                <span className="text-sm font-semibold">{item.label}</span>
                <span className="text-xs">
                  柜型：{cabinetTypeNames[item.cabinet_type_code]?.zh ?? item.cabinet_type_code}
                </span>
                <span className="text-xs">房间：{roomLabel(item.room, "zh") ?? "—"}</span>
                <span className="text-xs">
                  尺寸：{item.width_mm ?? "—"} × {item.height_mm ?? "—"} × {item.depth_mm ?? "—"} mm
                </span>
                <span className="text-xs">数量：{item.quantity}</span>
                <span className="text-xs">
                  门/抽/层板：{item.doors}/{item.drawers}/{item.shelves}
                </span>
                {item.rotation_deg ? <span className="text-xs">旋转：{item.rotation_deg}°</span> : null}
                <span className="ml-auto text-xs">
                  成本 {formatMoney(item.cost_cents, factoryPlan.cost_currency)} · 报价{" "}
                  {formatMoney(item.price_cents, factoryPlan.currency)}
                </span>
              </div>
              <Table className="mt-2">
                <thead>
                  <tr>
                    <Th className="text-black">组件</Th>
                    <Th className="text-black">计价方式</Th>
                    <Th className="text-black">计算公式 / 参数</Th>
                    <Th className="text-right text-black">数量</Th>
                    <Th className="text-black">单位</Th>
                    <Th className="text-right text-black">单价</Th>
                    <Th className="text-right text-black">金额</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((component) => (
                    <tr key={component.id}>
                      <Td>
                        <div className="flex flex-col">
                          <span>{component.name_snapshot}</span>
                          <span className="font-mono text-[11px] text-neutral-500">
                            {component.catalog_code} · 槽位 {component.slot}
                          </span>
                        </div>
                      </Td>
                      <Td className="font-mono text-xs">{component.calc_method}</Td>
                      <Td className="font-mono text-[11px]">{component.params}</Td>
                      <Td className="text-right tabular-nums">{component.quantity.toFixed(3)}</Td>
                      <Td className="text-xs">{component.unit}</Td>
                      <Td className="text-right tabular-nums">
                        {formatMoney(component.unit_price_cents, factoryPlan.cost_currency)}
                      </Td>
                      <Td className="text-right tabular-nums">
                        {formatMoney(component.amount_cents, factoryPlan.cost_currency)}
                      </Td>
                    </tr>
                  ))}
                  {rows.some((component) => component.state !== "ok") ? (
                    <tr>
                      <Td colSpan={7} className="text-xs text-amber-700">
                        有"待配置"行（缺尺寸 / 需手填面积 / 缺少依赖），金额按 0 计，不计入合计：
                        {rows
                          .filter((component) => component.state !== "ok")
                          .map((component) => `${component.slot}（${component.notes ?? "待配置"}）`)
                          .join("；")}
                      </Td>
                    </tr>
                  ) : null}
                  {rows.length === 0 ? (
                    <tr>
                      <Td colSpan={7} className="text-center text-xs text-neutral-500">
                        该柜体还没有组件明细（先点「生成报价」套用柜型默认组合）。
                      </Td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>
            </section>
          );
        })}

        {packagingTotals.length > 0 ? (
          <div className="mt-8 break-inside-avoid border border-black p-3 text-xs">
            <p className="font-semibold">包装备料（每组柜子按件计）</p>
            <Table className="mt-2">
              <thead>
                <tr>
                  <Th className="text-black">项目</Th>
                  <Th className="text-right text-black">组数</Th>
                  <Th className="text-right text-black">数量</Th>
                  <Th className="text-right text-black">成本</Th>
                </tr>
              </thead>
              <tbody>
                {packagingTotals.map((row) => (
                  <tr key={row.code}>
                    <Td>{row.name}</Td>
                    <Td className="text-right tabular-nums">{row.groups}</Td>
                    <Td className="text-right tabular-nums">{row.quantity}</Td>
                    <Td className="text-right tabular-nums">
                      {formatMoney(row.costCents, factoryPlan.cost_currency)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : null}

        <div className="mt-8 flex justify-end">
          <div className="w-80 space-y-1">
            <div className="flex justify-between text-sm">
              <span>成本合计</span>
              <span className="tabular-nums">
                {formatMoney(factoryPlan.total_cost_cents, factoryPlan.cost_currency)}
              </span>
            </div>
            <div className="mt-2 flex justify-between border-t border-black pt-2 text-base font-semibold">
              <span>客户报价合计</span>
              <span className="tabular-nums">
                {formatMoney(factoryPlan.total_price_cents, factoryPlan.currency)}
              </span>
            </div>
            <p className="pt-1 text-[11px] text-neutral-600">
              工厂口径只用于生产与核价：金额为成本币种；客户文档使用报价币种，隐藏成本与系数。
            </p>
          </div>
        </div>

        <footer className="mt-8 border-t border-neutral-300 pt-4 text-[11px] text-neutral-600">
          <p>本单为内部生产/核价底稿，请以确认后的生产图、样板与五金清单为准。</p>
        </footer>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[820px] bg-white p-8 text-[13px] leading-relaxed text-black print:max-w-none print:p-0">
      {toolbar}

      {/* 客户版先放"画了框的户型图页"，每页单独一张，后面才是报价单 */}
      {planPages.map((page, index) => (
        <section key={page.key} className="mb-6 break-after-page">
          <div className="mb-2 flex items-baseline justify-between border-b border-black/40 pb-1 text-xs">
            <span className="font-semibold">
              {t.floorPlan} {index + 1} / {planPages.length}
            </span>
            <span className="text-neutral-600">{page.pageLabel}</span>
          </div>
          <img src={page.dataUrl} alt={`Floor plan ${index + 1}`} className="w-full object-contain" />
          <p className="mt-2 text-[11px] text-neutral-600">{t.planNote}</p>
        </section>
      ))}

      <header className="flex items-start justify-between border-b-2 border-black pb-4">
        <div>
          <img src="/izza-logo.svg" alt="IZZA" className="mb-2 h-8 w-auto" />
          <h1 className="text-xl font-semibold tracking-wide">{t.quotation}</h1>
          <p className="mt-1 text-sm">{plan.name}</p>
          {plan.code ? <p className="text-xs text-neutral-600">Ref. {plan.code}</p> : null}
        </div>
        <div className="text-right text-xs text-neutral-600">
          <p>
            {t.date}: {today}
          </p>
          <p>
            {t.currency}: {currency}
          </p>
          <p>{t.terms}: EXW Foshan</p>
        </div>
      </header>

      <Table className="mt-6">
        <thead>
          <tr>
            <Th className="text-black">{t.item}</Th>
            <Th className="text-black">{t.room}</Th>
            <Th className="text-black">{t.type}</Th>
            <Th className="text-black">{t.size}</Th>
            <Th className="text-right text-black">{t.qty}</Th>
            <Th className="text-right text-black">{t.amount}</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const pending = item.size_status !== "confirmed";
            const lines = materialLines(item.materials);
            return (
              <Fragment key={item.id}>
                <tr>
                  <Td className="font-medium">{item.label}</Td>
                  <Td>{roomLabel(item.room, language) ?? "—"}</Td>
                  <Td>{typeText(item.cabinet_type_code)}</Td>
                  <Td className="tabular-nums">
                    {pending ? (
                      <Badge tone="warn" className="no-print">
                        TBC
                      </Badge>
                    ) : (
                      `${item.width_mm} × ${item.height_mm} × ${item.depth_mm ?? "—"}`
                    )}
                  </Td>
                  <Td className="text-right tabular-nums">{item.quantity}</Td>
                  <Td className="text-right tabular-nums">
                    {pending ? "to be confirmed" : formatMoney(item.price_cents, currency)}
                  </Td>
                </tr>
                {/*
                  含料明细挂在条目下面（柜体/门板/见光板/台面/五金/配件 + 含不含灯带）：
                  客户看的是"这个柜子用什么材料"，整单汇总反而对不上号。
                  名称来自服务端的组件快照，这里不做任何金额反推。
                */}
                {lines.length > 0 ? (
                  <tr>
                    <Td colSpan={6} className="px-0 py-0">
                      <dl className="my-1 ml-8 space-y-px border-l border-neutral-300 pl-4 text-[11px] leading-5">
                        {lines.map((line) => (
                          <div key={line.key} className="flex gap-3">
                            <dt className="w-20 shrink-0 text-neutral-500">{line.label}</dt>
                            <dd className="min-w-0 flex-1 text-neutral-800">{line.value}</dd>
                          </div>
                        ))}
                      </dl>
                    </Td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
          {items.length === 0 ? (
            <tr>
              <Td colSpan={6} className="text-center text-neutral-500">
                {t.noItems}
              </Td>
            </tr>
          ) : null}
        </tbody>
      </Table>

      {packaging.length > 0 ? (
        <div className="mt-4 rounded border border-neutral-300 p-3 text-xs">
          <p className="font-medium">{t.packaging}</p>
          <p className="text-neutral-600">{t.packagingNote}</p>
          <ul className="mt-1 space-y-0.5">
            {packaging.map((row) => (
              <li key={row.code} className="flex justify-between gap-4">
                <span>{language === "en" ? (row.name_en ?? row.name) : row.name}</span>
                <span className="tabular-nums">
                  {row.quantity} {t.pcs} · {formatMoney(row.price_cents, currency)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-4 flex justify-end">
        <div className="w-72 space-y-1">
          <div className="flex justify-between text-sm">
            <span>{t.subtotal}</span>
            <span className="tabular-nums">{formatMoney(plan.goodsCents, currency)}</span>
          </div>
          {/* 整装出货：货值之外单列一行，客户看得到多收了多少 */}
          {plan.assemblySurchargeCents > 0 ? (
            <div className="flex justify-between text-sm">
              <span>{t.assemblyLabel}</span>
              <span className="tabular-nums">{formatMoney(plan.assemblySurchargeCents, currency)}</span>
            </div>
          ) : null}
          {pendingItems.length > 0 ? (
            <div className="flex justify-between text-xs text-neutral-600">
              <span>{t.awaiting}</span>
              <span>
                {pendingItems.length} · {t.excluded}
              </span>
            </div>
          ) : null}
          <div className="mt-2 flex justify-between border-t border-black pt-2 text-base font-semibold">
            <span>
              {t.total} ({currency}, EXW Foshan)
            </span>
            <span className="tabular-nums">{formatMoney(plan.totalPriceCents, currency)}</span>
          </div>
        </div>
      </div>

      {/* 付款方式与交货期：客户下单前一定会问，报价单上必须写清楚 */}
      <div className="mt-4 rounded border border-neutral-300 p-3 text-xs">
        <p className="font-medium">{t.paymentDelivery}</p>
        <dl className="mt-1 space-y-0.5">
          <div className="flex gap-3">
            <dt className="w-24 shrink-0 text-neutral-500">{t.payment}</dt>
            <dd>{paymentTermsText}</dd>
          </div>
          <div className="flex gap-3">
            <dt className="w-24 shrink-0 text-neutral-500">{t.delivery}</dt>
            <dd>{t.deliveryTerms}</dd>
          </div>
        </dl>
      </div>

      {/* 报价说明：照客户在用的那份单据的写法，把"不含什么、按什么做、有效期"一次写清 */}
      <div className="mt-4 rounded border border-neutral-300 p-3 text-xs">
        <p className="font-medium">{t.notes}</p>
        <ol className="mt-1 list-decimal space-y-0.5 pl-4">
          {noteItems.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ol>
      </div>

      <footer className="mt-8 space-y-1 border-t border-neutral-300 pt-4 text-[11px] text-neutral-600">
        <p>{t.disclaimer}</p>
      </footer>
    </div>
  );
}
